<template>
  <div class="row">
    <div class="col-12 d-flex justify-content-between align-items-center mb-4">
      <h1 class="h2 mb-0">Users List</h1>
      <div class="d-flex gap-2">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="showDeactivated"
            @change="toggleShowDeactivated"
            id="showDeactivated"
          />
          <label class="form-check-label" for="showDeactivated">
            Show Deactivated
          </label>
        </div>
        <button @click="openNewUserModal" class="btn btn-primary">
          <i class="fas fa-plus me-2"></i>New User
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <ContentSkeleton v-if="loading" variant="table" :rows="6" :cols="5" />

    <!-- Error message -->
    <div v-if="error" class="alert alert-danger mb-4">
      {{ error }}
    </div>

    <!-- Users table -->
    <div v-if="!loading && !error" class="card shadow-sm">
      <div
        class="card-header bg-light d-flex justify-content-between align-items-center"
      >
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="isAllSelected"
            @change="toggleSelectAll"
            id="selectAll"
          />
          <label class="form-check-label" for="selectAll"> Select All </label>
        </div>
        <div v-if="selectedUsers.length > 0" class="d-flex gap-2">
          <button
            @click="deactivateSelectedUsers"
            class="btn btn-warning btn-sm"
          >
            Deactivate Selected ({{ selectedUsers.length }})
          </button>
          <button @click="deleteSelectedUsers" class="btn btn-danger btn-sm">
            Delete Selected ({{ selectedUsers.length }})
          </button>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th scope="col" style="width: 40px"></th>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in filteredUsers"
              :key="user._id"
              :class="{ 'table-secondary': user.status === 'deactivated' }"
            >
              <td>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="selectedUsers.includes(user._id)"
                    @change="toggleUserSelection(user._id)"
                  />
                </div>
              </td>
              <td>
                <div class="d-flex align-items-center">
                  <div class="flex-shrink-0 d-flex align-items-center gap-2">
                    <img
                      v-if="user.avatar"
                      :src="user.avatar"
                      class="rounded-circle"
                      width="40"
                      height="40"
                      alt="User avatar"
                    />
                    <div
                      v-else
                      class="rounded-circle bg-light d-flex align-items-center justify-content-center"
                      style="width: 40px; height: 40px"
                    >
                      <span class="text-muted">{{ user.name.charAt(0) }}</span>
                    </div>
                    <img
                      v-if="user.signature"
                      :src="user.signature"
                      class="rounded"
                      width="60"
                      height="30"
                      alt="User signature"
                      style="object-fit: contain; border: 1px solid #ddd"
                    />
                    <div
                      v-else
                      class="rounded bg-light d-flex align-items-center justify-content-center"
                      style="width: 60px; height: 30px; border: 1px solid #ddd"
                    >
                      <span class="text-muted" style="font-size: 0.7rem">No sig</span>
                    </div>
                  </div>
                  <div class="ms-3">
                    <div class="fw-medium">{{ user.name }}</div>
                  </div>
                </div>
              </td>
              <td>{{ user.email }}</td>
              <td>
                <span class="badge bg-success">{{ user.role }}</span>
              </td>
              <td>
                <span v-if="user.status === 'active'" class="badge bg-success"
                  >Active</span
                >
                <span v-else class="badge bg-secondary">Deactivated</span>
              </td>
              <td>
                <div class="btn-group" role="group">
                  <button
                    @click="openEditModal(user)"
                    class="btn btn-sm btn-outline-primary"
                  >
                    Edit
                  </button>
                  <button
                    v-if="user.status === 'active'"
                    @click="deactivateUser(user._id)"
                    class="btn btn-sm btn-outline-warning"
                  >
                    Deactivate
                  </button>
                  <button
                    v-else
                    @click="reactivateUser(user._id)"
                    class="btn btn-sm btn-outline-success"
                  >
                    Reactivate
                  </button>
                  <button
                    @click="deleteUserPermanent(user._id)"
                    class="btn btn-sm btn-outline-danger"
                  >
                    Delete
                  </button>
                  <button
                    v-if="isAdmin"
                    :disabled="loginLinkUserId === user._id"
                    @click="copyLoginUrl(user)"
                    class="btn btn-sm btn-outline-secondary"
                    title="Copia una URL de un solo uso para entrar como este usuario"
                  >
                    {{ loginLinkUserId === user._id ? "..." : "Login URL" }}
                  </button>
                  <button
                    v-if="user.twoFactor && user.twoFactor.enabled"
                    @click="resetTwoFactor(user)"
                    class="btn btn-sm btn-outline-secondary"
                    :title="$t('twoFactor.adminResetHint')"
                  >
                    {{ $t("twoFactor.adminReset") }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Edit User Modal -->
    <EditUserForm
      v-if="showEditModal"
      :show="showEditModal"
      @update:show="showEditModal = $event"
      :user="selectedUser"
      :roles="roles"
      @user-updated="handleUserUpdated"
    />

    <!-- New User Modal -->
    <EditUserForm
      v-if="showNewUserModal"
      :show="showNewUserModal"
      @update:show="showNewUserModal = $event"
      :roles="roles"
      @user-updated="handleUserCreated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import i18n from "@/i18n";
import { tn } from "@/i18n";
import { createAdminLoginLink } from "@/utils/adminLoginLink";
import EditUserForm from "@/components/users/EditUserForm.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import {
  getAllUsersAction,
  deactivateUserAction,
  deleteUserPermanentAction,
  reactivateUserAction,
} from "@/pages/Users/actions/user.action.js";

const users = ref([]);
const loading = ref(true);
const error = ref(null);
const selectedUsers = ref([]);
const showEditModal = ref(false);
const showNewUserModal = ref(false);
const selectedUser = ref(null);
const roles = ref([]);
const showDeactivated = ref(false);
const isAdmin = ref(false);
const loginLinkUserId = ref(null);

/** Para quien perdió el teléfono: después lo vuelve a activar el propio usuario. */
async function resetTwoFactor(user) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: tn("twoFactor.adminReset"),
    text: tn("twoFactor.adminResetConfirm", { name: user.name || user.email }),
    showCancelButton: true,
  });
  if (!isConfirmed) return;
  try {
    await wencesApi.post(`/api/users/${user._id}/2fa/reset`);
    user.twoFactor = { enabled: false };
  } catch (e) {
    Swal.fire({ icon: "error", title: e?.response?.data?.error || "Error" });
  }
}

/**
 * Copia una URL de un solo uso para entrar como ese usuario. Sirve para ver en producción
 * lo que ve él sin pedirle la contraseña. El enlace se quema al abrirlo.
 */
async function copyLoginUrl(user) {
  loginLinkUserId.value = user._id;
  try {
    const { url, ttlMinutes } = await createAdminLoginLink(user._id);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Sin permiso de portapapeles (o sin https) queda el texto para copiar a mano.
      await Swal.fire({
        icon: "info",
        title: "Login URL",
        input: "text",
        inputValue: url,
        text: "Copiala a mano: el navegador no dejó escribir en el portapapeles.",
      });
      return;
    }
    Swal.fire({
      icon: "success",
      title: "Login URL copiada",
      text:
        `Entra como ${user.name || user.email}. Un solo uso, vence en ` +
        (ttlMinutes >= 60
          ? `${Math.round((ttlMinutes / 60) * 10) / 10} h.`
          : `${ttlMinutes} minutos.`),
      timer: 3000,
      showConfirmButton: false,
    });
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: err?.response?.data?.error || err.message,
    });
  } finally {
    loginLinkUserId.value = null;
  }
}

const isAllSelected = computed(() => {
  return (
    filteredUsers.value.length > 0 &&
    selectedUsers.value.length === filteredUsers.value.length
  );
});

// Filtrar usuarios - ocultar usuarios admin si el usuario actual no es admin
const filteredUsers = computed(() => {
  if (isAdmin.value) {
    return users.value; // Mostrar todos los usuarios si es admin
  } else {
    // Filtrar usuarios que tengan roles con 'all_access'
    return users.value.filter((user) => {
      const userRole = roles.value.find((role) => role.name === user.role);
      if (
        userRole &&
        userRole.permissions &&
        Array.isArray(userRole.permissions)
      ) {
        return !userRole.permissions.includes("all_access");
      }
      // Si no se puede determinar el rol, mostrar el usuario (roles básicos)
      return true;
    });
  }
});

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedUsers.value = [];
  } else {
    selectedUsers.value = filteredUsers.value.map((user) => user._id);
  }
};

const toggleUserSelection = (userId) => {
  const index = selectedUsers.value.indexOf(userId);
  if (index === -1) {
    selectedUsers.value.push(userId);
  } else {
    selectedUsers.value.splice(index, 1);
  }
};

const toggleShowDeactivated = () => {
  showDeactivated.value = !showDeactivated.value;
  fetchUsers();
};

const openEditModal = (user) => {
  selectedUser.value = user;
  showEditModal.value = true;
};

const handleUserUpdated = (updatedUser) => {
  const index = users.value.findIndex((u) => u._id === updatedUser._id);
  if (index !== -1) {
    users.value[index] = updatedUser;
  }
};

const openNewUserModal = () => {
  selectedUser.value = null;
  showNewUserModal.value = true;
};

const handleUserCreated = (newUser) => {
  users.value.unshift(newUser);
  showNewUserModal.value = false;
  Swal.fire({
    title: "Success!",
    text: "User created successfully",
    icon: "success",
    position: "top",
  });
};

const deactivateSelectedUsers = async () => {
  const result = await Swal.fire({
    title: "¿Estás seguro?",
    text: `¿Deseas desactivar ${selectedUsers.value.length} usuarios?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f39c12",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
  });

  if (result.isConfirmed) {
    try {
      for (const userId of selectedUsers.value) {
        const response = await deactivateUserAction(userId);
        if (!response.ok) {
          throw new Error(response.message);
        }
      }
      await fetchUsers(); // Refresh the list
      selectedUsers.value = [];

      Swal.fire(
        "¡Desactivados!",
        "Los usuarios han sido desactivados correctamente.",
        "success"
      );
    } catch (err) {
      error.value = "Error deactivating users: " + err.message;
      Swal.fire("Error", "Hubo un error al desactivar los usuarios.", "error");
    }
  }
};

const deleteSelectedUsers = async () => {
  const result = await Swal.fire({
    title: "¿Estás seguro?",
    text: `¿Deseas eliminar DEFINITIVAMENTE ${selectedUsers.value.length} usuarios? Esta acción no se puede deshacer.`,
    icon: "error",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, eliminar definitivamente",
    cancelButtonText: "Cancelar",
  });

  if (result.isConfirmed) {
    try {
      for (const userId of selectedUsers.value) {
        const response = await deleteUserPermanentAction(userId);
        if (!response.ok) {
          throw new Error(response.message);
        }
      }
      await fetchUsers(); // Refresh the list
      selectedUsers.value = [];

      Swal.fire(
        "¡Eliminados!",
        "Los usuarios han sido eliminados definitivamente.",
        "success"
      );
    } catch (err) {
      error.value = "Error deleting users: " + err.message;
      Swal.fire("Error", "Hubo un error al eliminar los usuarios.", "error");
    }
  }
};

const fetchUsers = async () => {
  try {
    loading.value = true;
    const response = await getAllUsersAction(showDeactivated.value);
    if (response.ok) {
      users.value = response.users;
    } else {
      error.value = response.message;
    }
  } catch (err) {
    error.value =
      "Error loading users: " + (err.response?.data?.error || err.message);
  } finally {
    loading.value = false;
  }
};

const deactivateUser = async (userId) => {
  const result = await Swal.fire({
    title: "¿Estás seguro?",
    text: "¿Deseas desactivar este usuario?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f39c12",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
  });

  if (result.isConfirmed) {
    try {
      const response = await deactivateUserAction(userId);
      if (response.ok) {
        await fetchUsers(); // Refresh the list
        Swal.fire(
          "¡Desactivado!",
          "El usuario ha sido desactivado correctamente.",
          "success"
        );
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      error.value = "Error deactivating user: " + err.message;
      Swal.fire("Error", "Hubo un error al desactivar el usuario.", "error");
    }
  }
};

const reactivateUser = async (userId) => {
  const result = await Swal.fire({
    title: "¿Estás seguro?",
    text: "¿Deseas reactivar este usuario?",
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, reactivar",
    cancelButtonText: "Cancelar",
  });

  if (result.isConfirmed) {
    try {
      const response = await reactivateUserAction(userId);
      if (response.ok) {
        await fetchUsers(); // Refresh the list
        Swal.fire(
          "¡Reactivado!",
          "El usuario ha sido reactivado correctamente.",
          "success"
        );
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      error.value = "Error reactivating user: " + err.message;
      Swal.fire("Error", "Hubo un error al reactivar el usuario.", "error");
    }
  }
};

const deleteUserPermanent = async (userId) => {
  const result = await Swal.fire({
    title: "¿Estás seguro?",
    text: "¿Deseas eliminar DEFINITIVAMENTE este usuario? Esta acción no se puede deshacer.",
    icon: "error",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, eliminar definitivamente",
    cancelButtonText: "Cancelar",
  });

  if (result.isConfirmed) {
    try {
      const response = await deleteUserPermanentAction(userId);
      if (response.ok) {
        await fetchUsers(); // Refresh the list
        Swal.fire(
          "¡Eliminado!",
          "El usuario ha sido eliminado definitivamente.",
          "success"
        );
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      error.value = "Error deleting user: " + err.message;
      Swal.fire("Error", "Hubo un error al eliminar el usuario.", "error");
    }
  }
};

const fetchRoles = async () => {
  try {
    const response = await wencesApi.get("/api/roles");
    roles.value = response.data.data;
  } catch (err) {
    error.value =
      "Error loading roles: " + (err.response?.data?.error || err.message);
  }
};

// Obtener los permisos del usuario actual desde el backend
const fetchUserPermissions = async () => {
  try {
    const response = await wencesApi.get("/api/users/myprofile");
    if (response.data.ok) {
      const userData = response.data.data;
      let roleId;

      // Obtener el ID del rol
      if (typeof userData.role === "string") {
        roleId = userData.role;
      } else if (userData.role && userData.role._id) {
        roleId = userData.role._id;
      }

      if (roleId) {
        // Obtener los permisos del rol
        const roleResponse = await wencesApi.get(`/api/roles/${roleId}`);
        if (
          roleResponse.data.success &&
          roleResponse.data.data &&
          roleResponse.data.data.permissions
        ) {
          const permissions = roleResponse.data.data.permissions;
          // Verificar si tiene el permiso 'all_access'
          isAdmin.value = permissions.includes("all_access");
        }
      }
    }
  } catch (error) {
    console.error("Error fetching user permissions:", error);
    isAdmin.value = false;
  }
};

onMounted(() => {
  fetchUsers();
  fetchRoles();
  fetchUserPermissions();
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
